/**
 * Pricing Resolver Utility
 *
 * Feature: 005-price-grade-timeline
 * Implements date-aware price lookups for historical pricing support.
 */
import store from '@/store';
import moment from 'moment';
/** Epoch fallback for records without effectiveDate */
const EPOCH_DATE = '1970-01-01';
/**
 * Check if a pricing record matches the service criteria.
 */
function matchesServiceCriteria(pricing, lessonType, hourCount, studentCount) {
    return (pricing.data.lessonType === lessonType &&
        pricing.data.hourCount === hourCount &&
        pricing.data.studentCount === studentCount);
}
/**
 * Get the effective price for a service on a specific date.
 *
 * Finds the pricing record that matches the lesson type, hour count, and
 * student count, with the highest effectiveDate that is still <= the target date.
 *
 * @param lessonType - 'individual' | 'wex'
 * @param hourCount - Number of hours (1-8)
 * @param studentCount - Number of students (1-10)
 * @param date - Date for which to find the effective price
 * @returns The effective price record
 * @throws Error if no matching price is found
 */
export function getPriceForDate(lessonType, hourCount, studentCount, date) {
    const allPricing = store.getters['pricing/active'];
    const targetDateStr = date.format('YYYY-MM-DD');
    // Filter by matching lessonType, hourCount, studentCount AND effectiveDate <= date
    const matchingPrices = allPricing.filter((pricing) => {
        if (!matchesServiceCriteria(pricing, lessonType, hourCount, studentCount)) {
            return false;
        }
        // Treat missing effectiveDate as epoch (always valid)
        const effectiveDate = pricing.data.effectiveDate || EPOCH_DATE;
        return effectiveDate <= targetDateStr;
    });
    if (matchingPrices.length === 0) {
        // No matching version for target date - fallback to earliest available
        const allMatchingService = allPricing.filter((pricing) => matchesServiceCriteria(pricing, lessonType, hourCount, studentCount));
        if (allMatchingService.length === 0) {
            throw new Error(`Cena nebyla nalezena pro typ lekce "${lessonType}", ` +
                `pocet hodin ${hourCount}, pocet studentu ${studentCount}`);
        }
        // Return the earliest version (lowest effectiveDate)
        return allMatchingService.reduce((earliest, current) => {
            const earliestDate = earliest.data.effectiveDate || EPOCH_DATE;
            const currentDate = current.data.effectiveDate || EPOCH_DATE;
            return currentDate < earliestDate ? current : earliest;
        });
    }
    // Find the record with the highest effectiveDate (newest matching)
    return matchingPrices.reduce((newest, current) => {
        const newestDate = newest.data.effectiveDate || EPOCH_DATE;
        const currentDate = current.data.effectiveDate || EPOCH_DATE;
        return currentDate > newestDate ? current : newest;
    });
}
/**
 * Get the price value (number) for a service on a specific date.
 * Convenience wrapper that returns just the price amount.
 *
 * @param lessonType - 'individual' | 'wex'
 * @param hourCount - Number of hours (1-8)
 * @param studentCount - Number of students (1-10)
 * @param date - Date for which to find the effective price
 * @returns The price amount in CZK
 * @throws Error if no matching price is found
 */
export function getPriceAmountForDate(lessonType, hourCount, studentCount, date) {
    const pricing = getPriceForDate(lessonType, hourCount, studentCount, date);
    return pricing.data.price;
}
/**
 * Get the current effective price for a service (using today's date).
 * Convenience wrapper for getPriceForDate with moment().
 *
 * @param lessonType - 'individual' | 'wex'
 * @param hourCount - Number of hours (1-8)
 * @param studentCount - Number of students (1-10)
 * @returns The effective price record
 * @throws Error if no matching price is found
 */
export function getCurrentPrice(lessonType, hourCount, studentCount) {
    return getPriceForDate(lessonType, hourCount, studentCount, moment());
}
/**
 * Compare a historical price to the current price.
 *
 * @param lessonType - 'individual' | 'wex'
 * @param hourCount - Number of hours (1-8)
 * @param studentCount - Number of students (1-10)
 * @param historicalDate - Date of the historical price
 * @returns Object with historical and current prices, and whether they differ
 * @throws Error if no matching price is found for either date
 */
export function comparePriceToCurrentDate(lessonType, hourCount, studentCount, historicalDate) {
    const historicalPrice = getPriceAmountForDate(lessonType, hourCount, studentCount, historicalDate);
    const currentPrice = getPriceAmountForDate(lessonType, hourCount, studentCount, moment());
    return {
        historical: historicalPrice,
        current: currentPrice,
        isDifferent: historicalPrice !== currentPrice,
    };
}
